import { isNull } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useShefMenuItemsQueryLazyQuery } from 'src/gqlReactTypings.generated.d';
import { useCachedDishFirstFilters } from 'src/pages/consumer/dish-first/explore/hooks/useCachedDishFirst/useCachedDishFirstFilters';
import { NoMatchesBanner, NoPerfectMatchesBanner } from 'src/pages/consumer/order/components/ConsumerOrderBanner';
import { StyledChip } from 'src/shared/design-system/DishFirstCard';
import { LoadingWithHeight } from 'src/shared/design-system/Loading';
import { SpiceLevelMap } from 'src/shared/Enums';
import { match, P } from 'ts-pattern';
import { ConsumerOrderDishSelectionSection } from './ConsumerOrderDishSelectionSection';
import { IConsumerOrderDishSelectionProps } from './types';

const PARTIAL_KEY = 'partial';
const PERFECT_KEY = 'perfect';

export const FilteredConsumerOrderDishSelection: React.FC<IConsumerOrderDishSelectionProps> = ({
  shefId,
  deliveryDate,
  ...rest
}: IConsumerOrderDishSelectionProps) => {
  const { filters } = useCachedDishFirstFilters();
  const { clearQueryParams } = filters;

  const [runMenuItemsQuery, { data: menuItems, loading, error }] = useShefMenuItemsQueryLazyQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    runMenuItemsQuery({
      variables: {
        shefId,
        availableDate: deliveryDate?.toJSDate().toISOString(),
        filters: {
          dietaryFilters: filters.dietaryFilters,
          dishTypeFilters: filters.dishTypeFilters,
          spiceLevelFilters: filters.spiceLevelFilters.map((value) => SpiceLevelMap[value]),
          proteinFilters: filters.proteinFilters,
          minDishRating: filters.minRatingFilter,
          isEcoFriendlyPackaging: filters.ecoFilter,
          priceLevelFilters: filters.priceLevelFilters,
          segmentFilters: null,
          smallScreen: false,
        },
      },
    });
  }, [
    shefId,
    filters.dietaryFilters,
    filters.dishTypeFilters,
    filters.spiceLevelFilters,
    filters.proteinFilters,
    filters.minRatingFilter,
    filters.ecoFilter,
    filters.priceLevelFilters,
    deliveryDate,
    runMenuItemsQuery,
  ]);

  const perfectMatches = useMemo(
    () => (isNull(menuItems) ? [] : [...(menuItems?.shefMenuFilteredItems?.perfectMatches ?? [])]),
    [menuItems]
  );

  const partialMatches = useMemo(() => [...(menuItems?.shefMenuFilteredItems?.partialMatches ?? [])], [menuItems]);

  return match({ loading, perfectMatches, partialMatches, error })
    .with({ loading: true }, () => <LoadingWithHeight delayMs={500} />)
    .with({ error: P.not(P.nullish) }, () => <NoMatchesBanner onClick={clearQueryParams} />)
    .with({ perfectMatches: [], partialMatches: [] }, () => <NoMatchesBanner onClick={clearQueryParams} />)
    .with({ perfectMatches: [] }, () => (
      <>
        <NoPerfectMatchesBanner />
        <ConsumerOrderDishSelectionSection
          {...rest}
          key={'partial'}
          deliveryDate={deliveryDate}
          dishBadge={<StyledChip type='goodAlternative' text='Good Alternative' />}
          shefId={shefId}
          shefSegments={[]}
          attachAnchors={false}
          isUnavailable={rest.isUnavailable}
          foodItems={partialMatches}
          subTitle={'Good alternatives below that match some of your filters'}
        />
      </>
    ))
    .otherwise(() => (
      <>
        <ConsumerOrderDishSelectionSection
          key={PERFECT_KEY}
          {...rest}
          dishBadge={<StyledChip type='perfectMatch' text='Perfect Match' />}
          titleBadge={<StyledChip className={'mx-6'} type='perfectMatchTitle' text='Perfect Match' />}
          shefId={shefId}
          deliveryDate={deliveryDate}
          shefSegments={[]}
          attachAnchors={true}
          foodItems={perfectMatches}
          isUnavailable={rest.isUnavailable}
        />
        <ConsumerOrderDishSelectionSection
          {...rest}
          key={PARTIAL_KEY}
          deliveryDate={deliveryDate}
          dishBadge={<StyledChip type='goodAlternative' text='Good Alternative' />}
          shefId={shefId}
          shefSegments={[]}
          attachAnchors={false}
          isUnavailable={rest.isUnavailable}
          foodItems={partialMatches}
          subTitle={'Good alternatives below that match some of your filters'}
        />
      </>
    ));
};
